import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { TypingEngine } from '../../engine/TypingEngine.js';
import { formatDuration, formatPercent, starsFor } from '../../engine/metrics.js';
import { classify } from '../../engine/grading.js';
import { MODES } from '../../content/reference-modes.js';
import { CODE_TO_FINGER, FINGER_LABELS, findKeyForChar, resolveLayout } from '../../engine/layouts.js';
import type { FingerId } from '../../engine/layouts.js';
import { store, useStoreRevision } from '../store';
import { FingerLegend, Keyboard } from '../Keyboard';

interface PracticeViewProps {
  id: string;
  title: string;
  subtitle?: string;
  text: string;
  targets?: { cpm: number; accuracy: number };
  newKeys?: string[];
  /** Číslo lekcie 1–66 — podľa neho sa vyberá obdobie výučby pre známkovanie. */
  lessonNumber?: number;
  /** Identifikátor lekcie v adrese (pre prepínanie režimov). */
  lessonId?: string;
  /** Dostupné režimy cvičenia (hmaty, slova, vety, text). */
  modes?: string[];
  activeMode?: string;
  nextHref?: string | null;
  nextLabel?: string | null;
}

/**
 * Obrazovka cvičenia — jediné miesto, kde sa spracúva vstup z klávesnice.
 *
 * Vstupná logika (mŕtve klávesy, IME, paste, strata fokusu) patrí sem, pretože
 * engine je zámerne bez DOM. Diakritika na slovenskej klávesnici sa skladá ako
 * "mŕtvy kláves + znak" a prehliadač ju doručí až v druhom `keydown` — preto
 * udalosti s `key === 'Dead'` a `isComposing` ignorujeme a NEPOČÍTAME ich ako chybu.
 */
export function PracticeView(props: PracticeViewProps) {
  const { id, title, subtitle, text, targets, newKeys, lessonNumber, lessonId, modes, activeMode, nextHref, nextLabel } = props;

  useStoreRevision();
  const settings = store.getSettings();
  const layout = resolveLayout(settings.layoutId, store.calibration(settings.layoutId));

  const engine = useMemo(
    () =>
      new TypingEngine({
        text,
        errorMode: settings.errorMode,
        allowBackspace: settings.allowBackspace,
        tolerateDiacritics: settings.tolerateDiacritics,
        netPenalty: settings.netPenalty,
        // Oficiálne počítanie úderov: číslice a veľké písmená nie sú jeden úder.
        officialStrokes: settings.officialStrokes,
        // Pasívna kalibrácia: každý stlačený kláves prezradí, čo naozaj posiela
        // operačný systém. Aplikácia si tak sama opraví popisy klávesnice.
        onKeyObserved: (code, char) => {
          if (char.length !== 1 || char === ' ') return;
          const current = store.calibration(settings.layoutId);
          if (current[code] === char) return;
          store.setCalibration(settings.layoutId, { [code]: char });
        },
      }),
    // Zámerne bez `revision`: zmena nastavení nesmie vynulovať rozbehnuté cvičenie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, settings.errorMode, settings.allowBackspace, settings.tolerateDiacritics, settings.netPenalty, settings.officialStrokes, settings.layoutId],
  );

  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const [pressed, setPressed] = useState<string | null>(null);
  const [pasteAttempts, setPasteAttempts] = useState(0);
  const [outcome, setOutcome] = useState<{
    netCpm: number;
    stars: number;
    grade: number;
    label: string;
    period: number;
    speedNeeded: number;
  } | null>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const pressedTimer = useRef<number | null>(null);
  /**
   * Strážca proti dvojitému zápisu: v `StrictMode` React spúšťa efekty dvakrát,
   * pričom obe spustenia vidia rovnaký (ešte neaktualizovaný) stav. Bez tohto
   * by sa jeden dokončený pokus uložil dvakrát.
   */
  const savedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = engine.subscribe(forceRender);
    return () => {
      unsubscribe();
    };
  }, [engine]);

  // Živé metriky a "pauza" sa musia prekresľovať aj bez stlačenia klávesy.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (engine.startedAt !== null && !engine.finished) forceRender();
    }, 250);
    return () => window.clearInterval(timer);
  }, [engine]);

  useEffect(() => {
    savedRef.current = false;
    setOutcome(null);
    displayRef.current?.focus();
  }, [engine]);

  // Uloženie výsledku — presne raz na jedno dokončenie cvičenia.
  useEffect(() => {
    if (!engine.finished || savedRef.current) return;
    savedRef.current = true;
    const now = Date.now();
    const metrics = engine.liveMetrics(now);
    const result = engine.toResult(now, { id, title });
    const stars = targets ? starsFor(metrics, targets) : 0;
    // Známka podľa metodiky predlohy: hodnotí sa rýchlosť aj chybovosť, platí horšia.
    // Rýchlostné hranice závisia od obdobia výučby, do ktorého lekcia patrí.
    const classification = classify({
      netCpm: metrics.netCpm,
      errorRatePercent: metrics.errorRatePercent,
      targetSpeed: settings.targetSpeed,
      targetErrorRate: settings.targetErrorRate,
      lessonNumber,
    });
    store.recordAttempt({ ...result, stars, grade: classification.grade, mode: activeMode ?? null });
    setOutcome({
      netCpm: result.netCpm,
      stars,
      grade: classification.grade,
      label: classification.label,
      period: classification.period,
      speedNeeded: classification.speedNeeded,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, engine.finished, id, title]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const now = Date.now();

      // Systémové skratky a AltGr (na slovenskej klávesnici AltGr) nechávame OS.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      // Mŕtvy kláves / prebiehajúca kompozícia: nepočíta sa ako chyba.
      if (event.nativeEvent.isComposing || event.key === 'Dead') {
        event.preventDefault();
        return;
      }

      if (event.key === 'CapsLock') {
        // Zámka preraďovača sa podľa oficiálnych pravidiel počíta ako úder
        // (raz za zapnutie, raz za vypnutie).
        engine.addModifierStrokes(1);
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        engine.backspace(now);
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        engine.skip(now);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        engine.typeChar('Enter', now, 'Enter');
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        displayRef.current?.blur();
        return;
      }

      if (event.key.length === 1) {
        event.preventDefault();
        const result = engine.typeChar(event.key, now, event.code, {
          shift: event.shiftKey,
          // CapsLock sa počíta ako skupina veľkých písmen (oficiálne pravidlo).
          capsLock: typeof event.getModifierState === 'function' ? event.getModifierState('CapsLock') : false,
        });
        setPressed(event.code);
        if (pressedTimer.current !== null) window.clearTimeout(pressedTimer.current);
        pressedTimer.current = window.setTimeout(() => setPressed(null), 160);
        if (result === 'wrong') {
          // Jemné upozornenie pre čítačky obrazovky; vizuálne to rieši farba znaku.
          forceRender();
        }
      }
    },
    [engine],
  );

  const now = Date.now();
  const metrics = engine.liveMetrics(now);
  const isPaused = engine.startedAt !== null && !engine.finished && !engine.isActive(now);
  const expected = engine.expectedChar;
  const expectedKey = expected ? findKeyForChar(layout, expected) : null;
  const expectedFinger = expectedKey ? CODE_TO_FINGER.get(expectedKey.code) : undefined;
  const expectedLabel = expected === '\n' ? 'Enter' : expected;

  const lines = text.split('\n');
  let offset = 0;
  const renderedLines = lines.map((line, lineIndex) => {
    const start = offset;
    offset += line.length + 1;
    const segments: React.ReactNode[] = [];
    let i = 0;
    while (i < line.length) {
      const abs = start + i;
      const status = engine.status[abs];
      let j = i + 1;
      if (abs !== engine.index) {
        while (j < line.length && engine.status[start + j] === status && start + j !== engine.index) j += 1;
      }
      const className = [
        'char',
        status === 'correct' ? 'is-correct' : '',
        status === 'wrong' ? 'is-wrong' : '',
        abs === engine.index ? 'is-current' : '',
      ]
        .filter(Boolean)
        .join(' ');
      segments.push(
        <span key={abs} className={className}>
          {line.slice(i, j)}
        </span>,
      );
      i = j;
    }
    const newlineAbs = start + line.length;
    const newlineCurrent = engine.index === newlineAbs && lineIndex < lines.length - 1;
    return (
      <div key={lineIndex}>
        {segments}
        {lineIndex < lines.length - 1 && (
          <span className={`char is-newline ${newlineCurrent ? 'is-current' : ''}`} aria-hidden="true" />
        )}
      </div>
    );
  });

  return (
    <div className="card practice-card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <h2 style={{ marginBottom: 2 }}>{title}</h2>
          {subtitle && <div className="muted small">{subtitle}</div>}
        </div>
        {targets && (
          <div className="muted small">
            Cieľ: {targets.cpm} úhozov/min · presnosť {formatPercent(targets.accuracy)}
          </div>
        )}
      </div>

      {newKeys && newKeys.length > 0 && (
        <p className="small">
          <strong>Nové klávesy:</strong>{' '}
          {newKeys.map((k) => (
            <span key={k} className="chip" style={{ marginRight: 6 }}>
              <span className="k">{k === ' ' ? 'medzerník' : k}</span>
            </span>
          ))}
        </p>
      )}

      {lessonId && modes && modes.length > 1 && (
        <nav className="row" aria-label="Režimy cvičenia" style={{ margin: '4px 0 10px' }}>
          {modes.map((mode) => {
            const info = MODES.find((item) => item.slug === mode);
            const isActive = mode === activeMode;
            return (
              <a
                key={mode}
                className={`btn ${isActive ? 'btn-primary' : 'btn-ghost'}`}
                href={`#/cvicenie/${lessonId}/${mode}`}
                aria-current={isActive ? 'page' : undefined}
                title={info?.description}
              >
                {info?.label ?? mode}
              </a>
            );
          })}
          <span className="muted small">
            Hmaty = drily na znaky · Slová · Vety · Text (ako v predlohe)
          </span>
        </nav>
      )}

      <div className="metrics" style={{ margin: '12px 0' }}>
        <Metric label="Čistá rýchlosť" value={metrics.netCpm} unit="úderov/min" big />
        <Metric label="Hrubé údery" value={engine.grossStrokes} unit={`/ ${engine.keystrokes} stlačení`} />
        <Metric label="Chybovosť" value={metrics.errorRatePercent} unit="%" />
        <Metric label="Presnosť" value={Math.round(metrics.accuracy * 100)} unit="%" />
        <Metric label="Čas" value={formatDuration(metrics.durationMs)} />
        <Metric label="Chyby" value={engine.errors} />
      </div>

      {pasteAttempts > 0 && (
        <p className="pause-banner">
          Vkladanie textu je počas cvičenia zablokované ({pasteAttempts}×). Zmazané znaky sa nepočítajú ako úhozy.
        </p>
      )}
      {isPaused && <p className="pause-banner">Pauza — čas beží, až keď začneš písať.</p>}

      <div
        ref={displayRef}
        className="text-display"
        tabIndex={0}
        role="textbox"
        aria-label="Cvičný text na prepísanie"
        aria-readonly="true"
        onKeyDown={handleKeyDown}
        onPaste={(e) => {
          e.preventDefault();
          setPasteAttempts((n) => n + 1);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setPasteAttempts((n) => n + 1);
        }}
        onCut={(e) => e.preventDefault()}
      >
        {renderedLines}
      </div>

      <div className="row" style={{ marginBottom: 12 }}>
        <span className="muted small">
          {engine.finished ? (
            'Hotovo.'
          ) : (
            <>
              Na rade: <strong className="chip"><span className="k">{expectedLabel === ' ' ? 'medzerník' : expectedLabel}</span></strong>
              {expectedFinger && <> prstom: {FINGER_LABELS[expectedFinger as FingerId]}</>}
            </>
          )}
        </span>
        <span className="muted small">Vynechaný znak = Tab · prerušiť = Esc</span>
      </div>

      {settings.showKeyboard && (
        <>
          <Keyboard layout={layout} nextChar={expected} pressedCode={pressed} />
          <FingerLegend />
        </>
      )}

      {engine.errors > 0 && (
        <p className="small" style={{ marginTop: 12 }}>
          <strong>Najčastejšie chyby:</strong>{' '}
          {engine.worstKeys(5).map((w) => (
            <span key={w.char} className="chip" style={{ marginRight: 6 }}>
              <span className="k">{w.char === ' ' ? 'medzerník' : w.char}</span> {w.errors}×
            </span>
          ))}
        </p>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn"
          onClick={() => {
            engine.reset();
            savedRef.current = false;
            setPasteAttempts(0);
            setOutcome(null);
            displayRef.current?.focus();
          }}
        >
          Znova od začiatku
        </button>
        <a className="btn btn-ghost" href="#/lekcie">Zoznam lekcií</a>
        {engine.finished && nextHref && (
          <a className="btn btn-primary" href={nextHref}>{nextLabel ?? 'Ďalšia lekcia'}</a>
        )}
      </div>

      {engine.finished && outcome && (
        <div className="card" style={{ marginTop: 16, marginBottom: 0, background: 'var(--surface-2)' }}>
          <h3 style={{ marginTop: 0 }}>Výsledok</h3>
          <div className="metrics">
            <Metric label="Čistá rýchlosť" value={outcome.netCpm} unit="úderov/min" big />
            <Metric label="Chybovosť" value={metrics.errorRatePercent} unit="%" />
            <Metric label="Hrubé údery" value={engine.grossStrokes} />
            <Metric label="Chyby" value={engine.errors} />
            <Metric label="Chybné znaky" value={engine.status.filter((s) => s === 'wrong').length} />
            <Metric
              label={`Známka (cieľ ${settings.targetSpeed} úd/min, ${settings.targetErrorRate} %)`}
              value={`${outcome.grade} — ${outcome.label}`}
              big
            />
          </div>
          {targets && (
            <p className="stars" aria-label={`Hviezdičky: ${outcome.stars} z 3`}>
              {'★'.repeat(outcome.stars)}
              {'☆'.repeat(3 - outcome.stars)}
            </p>
          )}
          <p className="small muted" style={{ marginBottom: 0 }}>
            {outcome.grade > 1 && outcome.speedNeeded > outcome.netCpm && (
              <>Na stupeň {outcome.grade - 1} ti chýbalo {outcome.speedNeeded - outcome.netCpm} úderov/min.{' '}</>
            )}
            Hranice známok pochádzajú z tabuliek predlohy ({outcome.period}. obdobie výučby).{' '}
            Výsledok je uložený v tomto prehliadači.{' '}
            <a href="#/pokrok">Pozrieť vývoj pokroku</a>
          </p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, unit, big }: { label: string; value: number | string; unit?: string; big?: boolean }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className={`metric-value ${big ? 'is-big' : ''}`}>
        {value}
        {unit && <span className="small muted"> {unit}</span>}
      </div>
    </div>
  );
}
